import { formatDistanceToNow } from 'date-fns';
import { ArrowRight, Clock } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { StatusHistory, SubmissionStatus } from '@/types';

interface TeamStatusHistoryProps {
  history: StatusHistory[];
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isIsoDateString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime());
}

function ContextDetails({ context }: { context: unknown }) {
  const [showRaw, setShowRaw] = useState(false);

  if (context === null || context === undefined) return null;

  if (typeof context !== 'object' || Array.isArray(context)) {
    return (
      <div className="whitespace-pre-wrap rounded-md bg-muted px-2 py-1.5 font-mono text-xs">
        {typeof context === 'string'
          ? context
          : JSON.stringify(context, null, 2)}
      </div>
    );
  }

  const entries = Object.entries(context as Record<string, unknown>);
  if (entries.length === 0) return null;

  const ctx = context as Record<string, unknown>;
  const action = typeof ctx.action === 'string' ? ctx.action : null;
  const dateValue = entries.map(([, v]) => v).find((v) => isIsoDateString(v)) as
    | string
    | undefined;

  return (
    <div className="space-y-1 text-muted-foreground text-xs">
      <div className="flex items-center gap-2">
        {action || dateValue ? (
          <span>
            {action && <span className="font-mono">{action}</span>}
            {action && dateValue && ' @ '}
            {dateValue && new Date(dateValue).toLocaleString()}
          </span>
        ) : (
          <span>{entries.map(([k]) => humanizeKey(k)).join(', ')}</span>
        )}
        <button
          type="button"
          className="underline-offset-2 hover:underline"
          onClick={() => setShowRaw((v) => !v)}
        >
          {showRaw ? 'hide' : 'raw'}
        </button>
      </div>
      {showRaw && (
        <pre className="overflow-auto rounded-md bg-muted p-2 font-mono text-xs">
          {JSON.stringify(context, null, 2)}
        </pre>
      )}
    </div>
  );
}

const statusLabels: Record<SubmissionStatus, string> = {
  draft: 'Draft',
  received: 'Received',
  priority_waiting: 'Priority Waiting',
  asking_self_finance_trip: 'Asking Self Finance Trip',
  approved: 'Approved',
  onboarding_request: 'Onboarding Request',
  onboarding_expired: 'Onboarding Expired',
  onboarding_complete: 'Onboarding Complete',
  rejected: 'Rejected',
  waiting_list: 'Waiting List',
  withdrawn: 'Withdrawn',
  archived: 'Archived',
};

export function TeamStatusHistory({ history }: TeamStatusHistoryProps) {
  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Status History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No status changes recorded
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status History ({history.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {history.map((record) => (
            <div
              key={record.id}
              className="space-y-1 py-2 first:pt-0 last:pb-0"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <Badge variant="outline" className="text-xs">
                  {record.fromStatus
                    ? statusLabels[record.fromStatus as SubmissionStatus]
                    : 'None'}
                </Badge>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <Badge variant="outline" className="text-xs">
                  {statusLabels[record.toStatus as SubmissionStatus]}
                </Badge>
                <span className="ml-auto flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(record.changedAt), {
                    addSuffix: true,
                  })}
                  {record.changedByAdmin
                    ? ` · ${record.changedByAdmin.fullName}`
                    : ''}
                </span>
              </div>

              <ContextDetails context={record.context} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
